import { createGlobalContext } from '../registry'

export interface ComponentState {
  componentId: string
  componentType: string
  state: Record<string, unknown>
  metadata?: {
    resourceName?: string
    resourceUid?: string
    description?: string
    /**
     * Disambiguator for pages that mount more than one instance of the
     * same `componentType` (e.g. two `mortar.ListView`s, one per tab).
     * Producers tag their slot via `<PlatformComponent componentRole>`
     * (or by registering with `metadata.role` directly); consumers
     * resolve a specific slot via `useComponentByRole(componentType, role)`.
     */
    role?: string
    [key: string]: unknown
  }
}

/**
 * Snapshot type used by `usePageStateSelector` / `useSyncExternalStore`.
 * Same shape as the `components` map exposed on the context value.
 */
export type PageStateSnapshot = Record<string, ComponentState>

export interface PageStateContextValue {
  components: PageStateSnapshot
  registerComponent: (state: ComponentState) => void
  unregisterComponent: (componentId: string) => void
  mergeComponentState: (
    componentId: string,
    state: Partial<ComponentState['state']>,
  ) => void
  setComponentState: (
    componentId: string,
    state: ComponentState['state'],
  ) => void
  getComponentState: (componentId: string) => ComponentState | undefined
  getAllComponents: () => ComponentState[]
  /**
   * Subscribe to page-state mutations. Listener is invoked after every
   * register / unregister / merge / set. Used internally by
   * `usePageStateSelector` to drive `useSyncExternalStore` — consumers
   * shouldn't call this directly.
   */
  subscribe: (listener: () => void) => () => void
  /**
   * Snapshot accessor matching `useSyncExternalStore`'s contract.
   * Returns the same reference between mutations so React's bail-out
   * works. Internal — use `usePageStateSelector` instead.
   */
  getSnapshot: () => PageStateSnapshot
}

// Stable empty snapshot used by the default `getSnapshot`. The
// `useSyncExternalStore` contract requires `getSnapshot` to return the
// same reference between mutations so React's bail-out works; returning
// `{}` would allocate a fresh object on every call and trip the
// "getSnapshot should be cached" warning when consumers use the
// selector hooks outside a provider (e.g. SSR, test setup).
const EMPTY_PAGE_STATE_SNAPSHOT: PageStateSnapshot = {}

export const PageStateContext = createGlobalContext<PageStateContextValue>(
  'PageStateContext',
  {
    components: {},
    registerComponent: () => undefined,
    unregisterComponent: () => undefined,
    mergeComponentState: () => undefined,
    setComponentState: () => undefined,
    getComponentState: () => undefined,
    getAllComponents: () => [],
    subscribe: () => () => undefined,
    getSnapshot: () => EMPTY_PAGE_STATE_SNAPSHOT,
  },
)
